import React, { useState } from 'react';
import { clsx } from 'clsx';
import {
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Layers,
  LogOut,
  Settings,
} from 'lucide-react';

export interface NavChild {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

export interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  type: 'single' | 'section';
  children?: NavChild[];
  badge?: number;
}

export interface SidebarProps {
  appName: string;
  appSubtitle?: string;
  navItems: NavItem[];
  currentPage: string;
  onNavigate: (pageId: string) => void;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  userName?: string;
  userInitials?: string;
  userEmail?: string;
  userRole?: string;
  onLogout?: () => void;
  logoIcon?: React.ComponentType<{ className?: string }>;
  logoGradient?: string;
  onUserClick?: () => void;
  /** Badge counts keyed by nav item id — overrides static badge values */
  navBadges?: Record<string, number>;
}

export function Sidebar({
  appName,
  appSubtitle,
  navItems,
  currentPage,
  onNavigate,
  isDarkMode,
  onToggleTheme,
  collapsed,
  onToggleCollapse,
  userName,
  userInitials,
  userEmail,
  userRole,
  onLogout,
  logoIcon: LogoIcon,
  logoGradient,
  onUserClick,
  navBadges,
}: SidebarProps) {
  const getBadge = (id: string): number => navBadges?.[id] ?? 0;
  const [expandedSections, setExpandedSections] = useState<string[]>(
    navItems.filter((n) => n.type === 'section').map((n) => n.id)
  );

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((s) => s !== sectionId)
        : [...prev, sectionId]
    );
  };

  const isSectionActive = (children?: NavChild[]) =>
    children?.some((child) => currentPage === child.id) ?? false;

  return (
    <div
      className={clsx(
        'flex flex-col h-screen fixed left-0 top-0 transition-all duration-200 z-30 group/sidebar',
        collapsed ? 'w-16' : 'w-64',
        isDarkMode
          ? 'bg-gray-950 text-white'
          : 'bg-white text-gray-900 border-r border-gray-200'
      )}
    >
      {/* Collapse/expand arrow on sidebar edge */}
      <button
        onClick={onToggleCollapse}
        className={clsx(
          'absolute top-1/2 -translate-y-1/2 -right-3 w-6 h-6 rounded-full flex items-center justify-center z-40',
          'opacity-0 group-hover/sidebar:opacity-100 transition-opacity duration-200',
          'shadow-md border',
          isDarkMode
            ? 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700'
            : 'bg-white border-gray-200 text-gray-400 hover:text-gray-700 hover:bg-gray-50'
        )}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
      </button>
      {/* Hub link */}
      {!collapsed && (
        <a
          href="https://hub.nuance.team"
          className={clsx(
            'flex items-center gap-1.5 px-6 py-2 text-xs transition-colors no-underline border-b',
            isDarkMode
              ? 'text-gray-500 hover:text-gray-300 border-gray-800'
              : 'text-gray-400 hover:text-gray-600 border-gray-200'
          )}
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Hub
        </a>
      )}
      {collapsed && (
        <a
          href="https://hub.nuance.team"
          className={clsx(
            'flex items-center justify-center py-2 transition-colors no-underline border-b',
            isDarkMode
              ? 'text-gray-500 hover:text-gray-300 border-gray-800'
              : 'text-gray-400 hover:text-gray-600 border-gray-200'
          )}
          title="Back to Hub"
        >
          <ArrowLeft className="w-4 h-4" />
        </a>
      )}

      {/* Logo */}
      <div
        className={clsx(
          'p-4 border-b flex items-center',
          isDarkMode ? 'border-gray-800' : 'border-gray-200',
          collapsed ? 'justify-center' : 'gap-3 px-6'
        )}
      >
        <div className={clsx('w-10 h-10 bg-gradient-to-br rounded-lg flex items-center justify-center flex-shrink-0', logoGradient || 'from-blue-500 to-blue-600')}>
          {LogoIcon ? <LogoIcon className="w-6 h-6 text-white" /> : <Layers className="w-6 h-6 text-white" />}
        </div>
        {!collapsed && (
          <div>
            <h1
              className={clsx(
                'font-bold text-lg tracking-tight',
                isDarkMode ? 'text-white' : 'text-gray-900'
              )}
            >
              {appName}
            </h1>
            {appSubtitle && (
              <p
                className={clsx(
                  'text-xs uppercase tracking-wider',
                  isDarkMode ? 'text-gray-500' : 'text-gray-400'
                )}
              >
                {appSubtitle}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;

          if (item.type === 'single') {
            const isActive = currentPage === item.id;
            const badgeCount = getBadge(item.id);
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                title={collapsed ? item.label : undefined}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-150',
                  collapsed && 'justify-center',
                  isActive
                    ? 'bg-blue-600 text-white'
                    : isDarkMode
                      ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                )}
              >
                <span className="relative flex-shrink-0">
                  <Icon className="w-5 h-5" />
                  {collapsed && badgeCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </span>
                {!collapsed && (
                  <>
                    <span className="font-medium text-sm flex-1 text-left">{item.label}</span>
                    {badgeCount > 0 && (
                      <span className={clsx(
                        'min-w-[20px] h-5 flex items-center justify-center rounded-full text-[11px] font-bold px-1.5',
                        isActive ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                      )}>
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </>
                )}
              </button>
            );
          }

          // Section with children
          const isExpanded = expandedSections.includes(item.id);
          const hasActiveChild = isSectionActive(item.children);

          // Sum up badges for all children in this section
          const sectionBadgeTotal = item.children?.reduce((sum, c) => sum + getBadge(c.id), 0) ?? 0;

          if (collapsed) {
            // In collapsed mode, show children as direct icons
            return (
              <div key={item.id} className="space-y-1">
                {item.children?.map((child) => {
                  const ChildIcon = child.icon;
                  const isChildActive = currentPage === child.id;
                  const childBadge = getBadge(child.id);
                  return (
                    <button
                      key={child.id}
                      onClick={() => onNavigate(child.id)}
                      title={child.label}
                      className={clsx(
                        'w-full flex items-center justify-center px-3 py-2.5 rounded-lg transition-all duration-150',
                        isChildActive
                          ? 'bg-blue-600 text-white'
                          : isDarkMode
                            ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      )}
                    >
                      <span className="relative">
                        <ChildIcon className="w-5 h-5" />
                        {childBadge > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1">
                            {childBadge > 99 ? '99+' : childBadge}
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          }

          return (
            <div key={item.id} className="space-y-0.5">
              <button
                onClick={() => toggleSection(item.id)}
                className={clsx(
                  'w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-150',
                  hasActiveChild && !isExpanded
                    ? isDarkMode
                      ? 'bg-gray-800 text-white'
                      : 'bg-gray-100 text-gray-900'
                    : isDarkMode
                      ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                )}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="font-medium text-sm">{item.label}</span>
                  {sectionBadgeTotal > 0 && !isExpanded && (
                    <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold px-1.5">
                      {sectionBadgeTotal > 99 ? '99+' : sectionBadgeTotal}
                    </span>
                  )}
                </div>
                <ChevronDown
                  className={clsx(
                    'w-4 h-4 transition-transform duration-200',
                    isExpanded && 'rotate-180'
                  )}
                />
              </button>

              {isExpanded && (
                <div
                  className={clsx(
                    'ml-4 pl-4 space-y-0.5'
                  )}
                >
                  {item.children?.map((child) => {
                    const ChildIcon = child.icon;
                    const isChildActive = currentPage === child.id;
                    const childBadge = getBadge(child.id);
                    return (
                      <button
                        key={child.id}
                        onClick={() => onNavigate(child.id)}
                        className={clsx(
                          'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-150',
                          isChildActive
                            ? 'bg-blue-600 text-white'
                            : isDarkMode
                              ? 'text-gray-400 hover:bg-gray-800 hover:text-white'
                              : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                        )}
                      >
                        <ChildIcon className="w-4 h-4" />
                        <span className="text-sm flex-1 text-left">{child.label}</span>
                        {childBadge > 0 && (
                          <span className={clsx(
                            'min-w-[20px] h-5 flex items-center justify-center rounded-full text-[11px] font-bold px-1.5',
                            isChildActive ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                          )}>
                            {childBadge > 99 ? '99+' : childBadge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer: user */}
      <div
        className={clsx(
          'border-t p-2 space-y-1',
          isDarkMode ? 'border-gray-800' : 'border-gray-200'
        )}
      >
        {/* User */}
        {userName && (
          <div
            className={clsx(
              'flex items-center gap-3 px-3 py-2 rounded-lg',
              collapsed && 'justify-center',
              isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
            )}
          >
            <button
              onClick={onUserClick}
              className="group relative w-8 h-8 flex-shrink-0"
              title="User settings"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-medium text-xs group-hover:hidden">
                  {userInitials ?? userName.slice(0, 2).toUpperCase()}
                </span>
                <Settings className="w-4 h-4 text-white hidden group-hover:block" />
              </div>
            </button>
            {!collapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <p
                    className={clsx(
                      'text-sm font-medium truncate',
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    )}
                  >
                    {userName}
                  </p>
                  {userEmail && (
                    <p
                      className={clsx(
                        'text-xs truncate',
                        isDarkMode ? 'text-gray-500' : 'text-gray-400'
                      )}
                    >
                      {userEmail}
                    </p>
                  )}
                </div>
                {onLogout && (
                  <button
                    onClick={onLogout}
                    className={clsx(
                      isDarkMode
                        ? 'text-gray-500 hover:text-gray-300'
                        : 'text-gray-400 hover:text-gray-600'
                    )}
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
